import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Settings, BarChart2, Trophy, TrendingDown } from 'lucide-react';
import { getCosClassName, getCpaClassName, useComponentMetrics } from '../utils/metricsUtils';
import config from '../config';
import MetricsConfigModal from './MetricsConfigModal';
import ModuleVisibilityToggle from './ModuleVisibilityToggle';

import { calculateVariation, renderVariation } from '../utils/variationUtils';
import { getDateRangeFromPeriod, calculatePreviousPeriod } from '../utils/periodUtils';
/**
 * Composant pour afficher les tops et flops termes de recherche
 * Top termes = 5 termes les plus convertisseurs
 * Flop termes = 5 termes les plus cliqués sans conversion
 */
const TopFlopSearchTermsTable = React.memo(({
  selectedAccount,
  selectedPeriod,
  customStartDate,
  customEndDate,
  formatNumber,
  isVisible = true,
  onToggleVisibility,
  selectedCampaignFilter,
  selectedCampaignTypeFilter,
  campaignsData = [],
  performanceMaxData,
  token,
  setError,
  selectedLabelsFilter = [],
  campaignMatchesLabelsFilter = () => true,
  // Props pour preload (optimisation performance)
  preloadedData = null,
  // Props pour comparaison de périodes
  previousPeriodData = null,
  compareEnabled = false,
  comparisonType = '',
  userRole = 'USER',
  isClientModuleVisible,
  toggleClientModuleVisibility,
  moduleId = 'topflop_terms',
  loadingComparison = false
}) => {
  const [showMetricsConfigTop, setShowMetricsConfigTop] = useState(false);
  const [showMetricsConfigFlop, setShowMetricsConfigFlop] = useState(false);

  // Configuration des métriques pour chaque sous-composant
  const topMetrics = useComponentMetrics('topflop_terms_top', selectedAccount?.id || 'global');
  const flopMetrics = useComponentMetrics('topflop_terms_flop', selectedAccount?.id || 'global');

  // Helpers pour vérifier si une métrique est sélectionnée
  const isTopMetricVisible = useCallback((metricKey) => {
    return topMetrics.metrics.includes(metricKey);
  }, [topMetrics.metrics]);

  const isFlopMetricVisible = useCallback((metricKey) => {
    return flopMetrics.metrics.includes(metricKey);
  }, [flopMetrics.metrics]);

  const [loading, setLoading] = useState(false);
  const [loadingPreviousPeriod, setLoadingPreviousPeriod] = useState(false);
  // Stockage des termes bruts (non agrégés) pour permettre le filtrage par type avant agrégation
  const [allRawTerms, setAllRawTerms] = useState([]);
  // Stockage des termes bruts de la période précédente
  const [previousRawTerms, setPreviousRawTerms] = useState([]);





  // Fonction helper pour extraire les termes d'une réponse API
  const extractSearchTermsFromResponse = useCallback((data, performanceMaxAccountData) => {
    let allSearchTerms = [];

    Object.values(data.searchTermsResults).forEach(accountData => {
      let searchTermsArray = [];

      if (accountData.searchTermsData && accountData.searchTermsData.terms) {
        searchTermsArray = accountData.searchTermsData.terms;
      }
      else if (accountData.searchTerms && Array.isArray(accountData.searchTerms)) {
        searchTermsArray = accountData.searchTerms;
      }

      if (searchTermsArray.length > 0) {
        const searchTerms = searchTermsArray.map(term => ({
          searchTerm: term.searchTerm || term.term,
          // Les impressions étaient perdues ici : la régie les rend, cette normalisation ne les
          // recopiait pas, et les colonnes Impressions et CTR affichaient donc zéro pour tout
          // le monde — un zéro qui se lit comme une mesure, pas comme une absence.
          impressions: Number(term.impressions) || 0,
          clicks: Number(term.clicks) || 0,
          cost: Number(term.cost) || 0,
          conversions: Number(term.conversions) || 0,
          conversionsValue: Number(term.conversionsValue) || 0,
          conversionRate: Number(term.clicks) > 0 ? (Number(term.conversions) / Number(term.clicks)) * 100 : 0,
          cpa: Number(term.conversions) > 0 ? Number(term.cost) / Number(term.conversions) : 0,
          cos: Number(term.conversionsValue) > 0 ? (Number(term.cost) / Number(term.conversionsValue)) * 100 : 0,
          source: term.source || 'Search/Shopping',
          campaignId: term.campaignId,
          campaignName: term.campaignName
        }));
        allSearchTerms = allSearchTerms.concat(searchTerms);
      }
    });

    // Ajouter les données Performance Max si disponibles
    if (performanceMaxAccountData && performanceMaxAccountData.searchTerms) {
      if (Array.isArray(performanceMaxAccountData.searchTerms)) {
        const performanceMaxTerms = performanceMaxAccountData.searchTerms.map(term => ({
          searchTerm: term.searchTerm || term.term,
          // Les impressions étaient perdues ici : la régie les rend, cette normalisation ne les
          // recopiait pas, et les colonnes Impressions et CTR affichaient donc zéro pour tout
          // le monde — un zéro qui se lit comme une mesure, pas comme une absence.
          impressions: Number(term.impressions) || 0,
          clicks: Number(term.clicks) || 0,
          cost: Number(term.cost) || 0,
          conversions: Number(term.conversions) || 0,
          conversionsValue: Number(term.conversionsValue) || 0,
          conversionRate: Number(term.clicks) > 0 ? (Number(term.conversions) / Number(term.clicks)) * 100 : 0,
          cpa: Number(term.conversions) > 0 ? Number(term.cost) / Number(term.conversions) : 0,
          cos: Number(term.conversionsValue) > 0 ? (Number(term.cost) / Number(term.conversionsValue)) * 100 : 0,
          source: 'Performance Max',
          campaignId: term.campaignId,
          campaignName: term.campaignName
        }));

        allSearchTerms = allSearchTerms.concat(performanceMaxTerms);
      }
    }

    return allSearchTerms;
  }, []);

  // Fonction pour charger les données des termes de recherche
  const loadSearchTermsData = async () => {
    if (!selectedAccount || !token) {
      return;
    }

    setLoading(true);

    try {
      // Préparer le body de la requête (même structure que loadAllSearchTerms)
      const requestBody = {
        sources: [selectedAccount?.id],
        period: selectedPeriod,
        limit: 10000, // Limite réduite car on veut juste les tops/flops
        minClicks: 1 // Exclure les termes à 0 clics
      };

      // Ajouter les dates personnalisées si période CUSTOM
      if (selectedPeriod === 'CUSTOM') {
        if (!customStartDate || !customEndDate) {
          console.error('Dates personnalisées manquantes');
          return;
        }
        requestBody.customStartDate = customStartDate;
        requestBody.customEndDate = customEndDate;
      }

      const response = await fetch(`${config.API_URL}/api/dashboard/search-terms`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error(`Erreur ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.success && data.searchTermsResults) {
        const performanceMaxAccountData = performanceMaxData && selectedAccount
          ? (performanceMaxData[selectedAccount.id] || performanceMaxData[selectedAccount.name])
          : null;

        let allSearchTerms = extractSearchTermsFromResponse(data, performanceMaxAccountData);

        // Filtrer par campagne AVANT l'agrégation
        let filteredSearchTerms = allSearchTerms;
        if (selectedCampaignFilter && selectedCampaignFilter !== 'all') {
          let filterCampaignId;
          if (typeof selectedCampaignFilter === 'object') {
            filterCampaignId = (selectedCampaignFilter.campaignId || selectedCampaignFilter.id)?.toString();
          } else {
            filterCampaignId = selectedCampaignFilter?.toString();
          }

          console.log('🔍 [TOP-FLOP] Filtrage par campagne:', filterCampaignId, 'sur', allSearchTerms.length, 'termes');

          filteredSearchTerms = allSearchTerms.filter(term => {
            const termCampaignId = term.campaignId?.toString();
            return termCampaignId === filterCampaignId;
          });

          console.log('🔍 [TOP-FLOP] Après filtrage par campagne:', filteredSearchTerms.length, 'termes');
        }

        // Stocker les termes bruts - le filtrage par TYPE et l'agrégation seront faits dans useMemo
        setAllRawTerms(filteredSearchTerms);

        // Charger les données de la période précédente si comparaison activée
        if (compareEnabled && comparisonType) {
          setLoadingPreviousPeriod(true);
          let currentStartDate = customStartDate;
          let currentEndDate = customEndDate;
          if (!currentStartDate || !currentEndDate) {
            const dateRange = getDateRangeFromPeriod(selectedPeriod);
            currentStartDate = dateRange.startDate;
            currentEndDate = dateRange.endDate;
          }
          const prevPeriod = calculatePreviousPeriod(currentStartDate, currentEndDate, comparisonType);
          console.log(`🔍 [TOP-FLOP] Chargement période précédente: ${prevPeriod.startDate} à ${prevPeriod.endDate}`);

          try {
            const prevRequestBody = {
              sources: [selectedAccount?.id],
              period: 'CUSTOM',
              limit: 10000,
              minClicks: 1,
              customStartDate: prevPeriod.startDate,
              customEndDate: prevPeriod.endDate
            };

            const prevResponse = await fetch(`${config.API_URL}/api/dashboard/search-terms`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify(prevRequestBody)
            });

            if (prevResponse.ok) {
              const prevData = await prevResponse.json();

              if (prevData.success && prevData.searchTermsResults) {
                let prevAllSearchTerms = extractSearchTermsFromResponse(prevData, performanceMaxAccountData);

                // Appliquer le même filtre par campagne
                let prevFilteredSearchTerms = prevAllSearchTerms;
                if (selectedCampaignFilter && selectedCampaignFilter !== 'all') {
                  let filterCampaignId;
                  if (typeof selectedCampaignFilter === 'object') {
                    filterCampaignId = (selectedCampaignFilter.campaignId || selectedCampaignFilter.id)?.toString();
                  } else {
                    filterCampaignId = selectedCampaignFilter?.toString();
                  }

                  prevFilteredSearchTerms = prevAllSearchTerms.filter(term => {
                    const termCampaignId = term.campaignId?.toString();
                    return termCampaignId === filterCampaignId;
                  });
                }

                console.log(`🔍 [TOP-FLOP] ${prevFilteredSearchTerms.length} termes période précédente`);
                setPreviousRawTerms(prevFilteredSearchTerms);
              }
            }
          } catch (prevError) {
            console.warn(`⚠️ [TOP-FLOP] Erreur chargement période précédente:`, prevError.message);
            setPreviousRawTerms([]);
          } finally {
            setLoadingPreviousPeriod(false);
          }
        } else {
          setPreviousRawTerms([]);
        }
      }

    } catch (error) {
      console.error('❌ [TOP-FLOP-SEARCH-TERMS] Erreur:', error);
      if (setError) {
        setError(`Erreur lors du chargement des termes de recherche: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Charger les données quand le composant est monté ou quand les paramètres changent
  useEffect(() => {
    if (!isVisible) return;

    // Fonction pour charger les données de comparaison
    const loadPreviousPeriodSearchTerms = async () => {
      if (!compareEnabled || !selectedAccount || !token) {
        setPreviousRawTerms([]);
        return;
      }

      // Utiliser previousPeriodData.period si disponible (cohérence avec App.js)
      let prevPeriod;
      if (previousPeriodData?.period?.startDate && previousPeriodData?.period?.endDate) {
        prevPeriod = {
          startDate: previousPeriodData.period.startDate,
          endDate: previousPeriodData.period.endDate
        };
      } else if (comparisonType) {
        let currentStartDate = customStartDate;
        let currentEndDate = customEndDate;
        if (!currentStartDate || !currentEndDate) {
          const dateRange = getDateRangeFromPeriod(selectedPeriod);
          currentStartDate = dateRange.startDate;
          currentEndDate = dateRange.endDate;
        }
        prevPeriod = calculatePreviousPeriod(currentStartDate, currentEndDate, comparisonType);
      } else {
        setPreviousRawTerms([]);
        return;
      }

      console.log(`🔍 [TOP-FLOP] Chargement période précédente: ${prevPeriod.startDate} à ${prevPeriod.endDate}`);
      setLoadingPreviousPeriod(true);

      try {
        const prevRequestBody = {
          sources: [selectedAccount?.id],
          period: 'CUSTOM',
          limit: 10000,
          minClicks: 1,
          customStartDate: prevPeriod.startDate,
          customEndDate: prevPeriod.endDate
        };

        const prevResponse = await fetch(`${config.API_URL}/api/dashboard/search-terms`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(prevRequestBody)
        });

        if (prevResponse.ok) {
          const prevData = await prevResponse.json();
          if (prevData.success && prevData.searchTermsResults) {
            const performanceMaxAccountData = performanceMaxData && selectedAccount
              ? (performanceMaxData[selectedAccount.id] || performanceMaxData[selectedAccount.name])
              : null;
            let prevAllSearchTerms = extractSearchTermsFromResponse(prevData, performanceMaxAccountData);

            // Appliquer le même filtre par campagne
            if (selectedCampaignFilter && selectedCampaignFilter !== 'all') {
              let filterCampaignId;
              if (typeof selectedCampaignFilter === 'object') {
                filterCampaignId = (selectedCampaignFilter.campaignId || selectedCampaignFilter.id)?.toString();
              } else {
                filterCampaignId = selectedCampaignFilter?.toString();
              }
              prevAllSearchTerms = prevAllSearchTerms.filter(term => term.campaignId?.toString() === filterCampaignId);
            }

            console.log(`🔍 [TOP-FLOP] ${prevAllSearchTerms.length} termes période précédente`);
            setPreviousRawTerms(prevAllSearchTerms);
          }
        }
      } catch (prevError) {
        console.warn(`⚠️ [TOP-FLOP] Erreur chargement période précédente:`, prevError.message);
        setPreviousRawTerms([]);
      } finally {
        setLoadingPreviousPeriod(false);
      }
    };

    // Si preloadedData disponible et pas de filtres actifs, l'utiliser directement
    if (preloadedData && preloadedData.success && !selectedCampaignFilter && !selectedCampaignTypeFilter && selectedLabelsFilter.length === 0) {
      const performanceMaxAccountData = performanceMaxData && selectedAccount
        ? (performanceMaxData[selectedAccount.id] || performanceMaxData[selectedAccount.name])
        : null;
      const allSearchTerms = extractSearchTermsFromResponse(preloadedData, performanceMaxAccountData);
      console.log(`🔍 [TOP-FLOP-FRONTEND] Utilisation données préchargées: ${allSearchTerms.length} termes`);
      setAllRawTerms(allSearchTerms);
      // Charger les données de comparaison si activé
      if (compareEnabled && previousPeriodData?.period?.startDate && previousPeriodData?.period?.endDate) {
        loadPreviousPeriodSearchTerms();
      } else {
        setPreviousRawTerms([]);
      }
      return;
    }

    if (selectedAccount) {
      loadSearchTermsData();
    }
  }, [selectedAccount, selectedPeriod, customStartDate, customEndDate, selectedCampaignFilter, selectedCampaignTypeFilter, selectedLabelsFilter, performanceMaxData, isVisible, compareEnabled, comparisonType, preloadedData, extractSearchTermsFromResponse, token, previousPeriodData?.period?.startDate, previousPeriodData?.period?.endDate]);

  // Fonction helper pour filtrer et agréger les termes
  const filterAndAggregateTerms = useCallback((rawTerms) => {
    let termsToProcess = [...rawTerms];

    // ÉTAPE 1: Filtrer par TYPE de campagne sur les termes bruts (avant agrégation)
    if (selectedCampaignTypeFilter && termsToProcess.length > 0) {
      const filterType = selectedCampaignTypeFilter.type;
      const filterTypeName = selectedCampaignTypeFilter.name;

      // Type 10 = Performance Max → filtrer par source 'Performance Max'
      if (filterType === 10 || filterTypeName === 'Performance Max') {
        termsToProcess = termsToProcess.filter(term => term.source === 'Performance Max');
      } else {
        const matchingCampaignIds = (campaignsData || [])
          .filter(campaign => {
            const campaignType = campaign.advertisingChannelType || campaign.type || campaign.campaignType;
            return campaignType === filterType ||
                   String(campaignType) === String(filterType) ||
                   campaignType === filterTypeName ||
                   String(campaignType) === String(filterTypeName);
          })
          .map(campaign => String(campaign.id || campaign.campaignId));

        if (matchingCampaignIds.length > 0) {
          termsToProcess = termsToProcess.filter(term => {
            const termCampaignId = String(term.campaignId);
            return matchingCampaignIds.includes(termCampaignId);
          });
        } else {
          if (filterType === 2 || filterType === 4 ||
              filterTypeName === 'Recherche' || filterTypeName === 'Shopping') {
            termsToProcess = termsToProcess.filter(term =>
              term.source === 'Search/Shopping' || !term.source
            );
          }
        }
      }
    }

    // Filtrer par labels si des labels sont sélectionnés
    if (selectedLabelsFilter.length > 0) {
      termsToProcess = termsToProcess.filter(term => {
        const termCampaignId = term.campaignId;
        return campaignMatchesLabelsFilter(termCampaignId);
      });
    }

    // ÉTAPE 2: Agréger les termes filtrés par terme de recherche
    const aggregatedTerms = {};

    termsToProcess.forEach((term) => {
      const rawSearchTerm = term.searchTerm || term.term;
      if (!rawSearchTerm) return;

      const normalizedTerm = rawSearchTerm.trim().toLowerCase();
      const displayTerm = rawSearchTerm.trim();

      if (!aggregatedTerms[normalizedTerm]) {
        aggregatedTerms[normalizedTerm] = {
          searchTerm: displayTerm,
          normalizedTerm: normalizedTerm,
          // Un même terme revient par campagne, par groupe d'annonces, par jour et par appareil :
          // ses impressions s'additionnent comme ses clics. Les omettre ici les remettait à zéro
          // après coup, quelle que soit la valeur reçue de la régie.
          impressions: 0,
          clicks: 0,
          cost: 0,
          conversions: 0,
          conversionsValue: 0,
          campaignIds: new Set(),
          campaignNames: new Set(),
          sources: new Set()
        };
      }

      const agg = aggregatedTerms[normalizedTerm];
      agg.impressions += term.impressions || 0;
      agg.clicks += term.clicks || 0;
      agg.cost += term.cost || 0;
      agg.conversions += term.conversions || 0;
      agg.conversionsValue += term.conversionsValue || 0;

      if (term.campaignId) agg.campaignIds.add(term.campaignId);
      if (term.campaignName) agg.campaignNames.add(term.campaignName);
      if (term.source) agg.sources.add(term.source);
    });

    // Finaliser l'agrégation avec les métriques dérivées
    return Object.values(aggregatedTerms).map(term => ({
      ...term,
      campaignIds: Array.from(term.campaignIds),
      campaignNames: Array.from(term.campaignNames),
      sources: Array.from(term.sources),
      conversionRate: term.clicks > 0 ? (term.conversions / term.clicks) * 100 : 0,
      cpa: term.conversions > 0 ? term.cost / term.conversions : 0,
      cos: term.conversionsValue > 0 ? (term.cost / term.conversionsValue) * 100 : 0,
      // Le CTR se recalcule sur les totaux agrégés : celui d'une ligne isolée ne vaut que pour
      // sa campagne, et la moyenne des CTR n'est pas le CTR de la moyenne.
      ctr: term.impressions > 0 ? (term.clicks / term.impressions) * 100 : 0
    }));
  }, [selectedCampaignTypeFilter, campaignsData, selectedLabelsFilter, campaignMatchesLabelsFilter]);

  // Filtrage par TYPE de campagne, agrégation et calcul des top/flop
  const { filteredTopSearchTerms, filteredFlopSearchTerms, previousAggregatedTerms } = useMemo(() => {
    // Agréger les termes de la période actuelle
    const aggregatedTermsArray = filterAndAggregateTerms(allRawTerms);

    // Agréger les termes de la période précédente
    const prevAggregatedTermsArray = compareEnabled && previousRawTerms.length > 0
      ? filterAndAggregateTerms(previousRawTerms)
      : [];

    // ÉTAPE 3: Calculer les tops et flops
    const topTerms = aggregatedTermsArray
      .filter(term => term.conversions > 0)
      .sort((a, b) => b.conversions - a.conversions)
      .slice(0, 5);

    const flopTerms = aggregatedTermsArray
      .filter(term => term.conversions === 0 && term.clicks > 0)
      .sort((a, b) => b.clicks - a.clicks)
      .slice(0, 5);

    return {
      filteredTopSearchTerms: topTerms,
      filteredFlopSearchTerms: flopTerms,
      previousAggregatedTerms: prevAggregatedTermsArray
    };
  }, [allRawTerms, previousRawTerms, filterAndAggregateTerms, compareEnabled]);

  // Fonction pour trouver le terme correspondant dans la période précédente
  const findPreviousTerm = useCallback((currentTerm) => {
    if (!compareEnabled || !previousAggregatedTerms.length) return null;
    return previousAggregatedTerms.find(
      prevTerm => prevTerm.normalizedTerm === currentTerm.normalizedTerm
    );
  }, [compareEnabled, previousAggregatedTerms]);

  // Pour les clients : masquer complètement si le module est fermé
  if (userRole === 'CLIENT' && !isVisible) {
    return null;
  }

  // Masquer le composant si aucune donnée n'est disponible (après chargement)
  if (!loading && allRawTerms.length === 0) {
    return null;
  }

  return (
    <div className="campaigns-table-section">
      <div className="section-header">
        <h4><BarChart2 size={16} />&nbsp;Top & Flop Termes de recherche</h4>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {toggleClientModuleVisibility ? (
            <ModuleVisibilityToggle
              isVisible={isClientModuleVisible ? isClientModuleVisible(moduleId) : true}
              onToggle={toggleClientModuleVisibility}
              userRole={userRole}
              moduleId={moduleId}
              title="Top/Flop Termes de recherche"
            />
          ) : (
            <button
              onClick={onToggleVisibility}
              className="section-toggle-btn"
              title={isVisible ? "Masquer la section" : "Afficher la section"}
            >
              {isVisible ? '−' : '+'}
            </button>
          )}
        </div>
      </div>

      {isVisible && (
        <div className="table-container" style={{ border: 'none', boxShadow: 'none', background: 'transparent' }}>
          {loading ? (
            <div className="loading-state" style={{ padding: '20px', textAlign: 'center' }}>
              <div className="loading-spinner" style={{ margin: '0 auto 10px' }}></div>
              Chargement des termes de recherche...
            </div>
          ) : compareEnabled && (loadingComparison || loadingPreviousPeriod) ? (
            <div className="loading-message" style={{ textAlign: 'center', padding: '20px' }}>
              <div className="loading-spinner" style={{ margin: '0 auto 10px' }}></div>
              Chargement des données de comparaison...
            </div>
          ) : (
            <div className="paire-top-flop" style={{ display: 'flex', gap: '20px' }}>
            {/* Top Termes de recherche */}
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '0 0 15px 0' }}>
                <h5 style={{ margin: 0, color: '#2e7d32', fontSize: '1.1em' }}>
                  <Trophy size={16} />&nbsp;Top 5 Termes (Plus convertisseurs)
                </h5>
                {userRole !== 'CLIENT' && (
                  <button
                    onClick={() => setShowMetricsConfigTop(true)}
                    className="metrics-settings-btn"
                    title="Configurer les métriques"
                    style={{ padding: '4px', borderRadius: '4px' }}
                  >
                    <Settings size={14} />
                  </button>
                )}
              </div>
              <div className="campaigns-table-wrapper">
                <table className="campaigns-table">
                  <thead style={{ background: 'white' }}>
                    <tr>
                      <th style={{ background: 'white' }}>Terme de recherche</th>
                      {isTopMetricVisible('impressions') && <th className="numeric" style={{ background: 'white' }}>Impr.</th>}
                      {isTopMetricVisible('clics') && <th className="numeric" style={{ background: 'white' }}>Clics</th>}
                      {isTopMetricVisible('ctr') && <th className="numeric" style={{ background: 'white' }}>CTR</th>}
                      {isTopMetricVisible('cpc') && <th className="numeric" style={{ background: 'white' }}>CPC</th>}
                      {isTopMetricVisible('cout') && <th className="numeric" style={{ background: 'white' }}>Coût</th>}
                      {isTopMetricVisible('taux_conv') && <th className="numeric" style={{ background: 'white' }}>Taux conv.</th>}
                      {isTopMetricVisible('conv') && <th className="numeric" style={{ background: 'white' }}>Conv.</th>}
                      {isTopMetricVisible('cpa') && <th className="numeric" style={{ background: 'white' }}>CPA</th>}
                      {isTopMetricVisible('valeur_conv') && <th className="numeric" style={{ background: 'white' }}>Valeur Conv.</th>}
                      {isTopMetricVisible('cos') && <th className="numeric" style={{ background: 'white' }}>COS</th>}
                      {isTopMetricVisible('panier_moyen') && <th className="numeric" style={{ background: 'white' }}>Panier moy.</th>}
                      {isTopMetricVisible('roas') && <th className="numeric" style={{ background: 'white' }}>ROAS</th>}
                      {isTopMetricVisible('roi') && <th className="numeric" style={{ background: 'white' }}>ROI</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTopSearchTerms.length === 0 ? (
                      <tr>
                        <td colSpan={1 + (isTopMetricVisible('impressions') ? 1 : 0) + (isTopMetricVisible('clics') ? 1 : 0) + (isTopMetricVisible('ctr') ? 1 : 0) + (isTopMetricVisible('cpc') ? 1 : 0) + (isTopMetricVisible('cout') ? 1 : 0) + (isTopMetricVisible('taux_conv') ? 1 : 0) + (isTopMetricVisible('conv') ? 1 : 0) + (isTopMetricVisible('cpa') ? 1 : 0) + (isTopMetricVisible('valeur_conv') ? 1 : 0) + (isTopMetricVisible('cos') ? 1 : 0) + (isTopMetricVisible('panier_moyen') ? 1 : 0) + (isTopMetricVisible('roas') ? 1 : 0) + (isTopMetricVisible('roi') ? 1 : 0)} style={{ textAlign: 'center', color: '#666' }}>
                          Aucune donnée disponible
                        </td>
                      </tr>
                    ) : (
                      filteredTopSearchTerms.map((term, index) => {
                        const prevTerm = findPreviousTerm(term);
                        const hasComparison = !!prevTerm;
                        const prevConversionRate = prevTerm && prevTerm.clicks > 0 ? (prevTerm.conversions / prevTerm.clicks) * 100 : 0;
                        const prevCpa = prevTerm && prevTerm.conversions > 0 ? prevTerm.cost / prevTerm.conversions : 0;
                        const prevCos = prevTerm && prevTerm.conversionsValue > 0 ? (prevTerm.cost / prevTerm.conversionsValue) * 100 : 0;
                        // Métriques dérivées : le serveur ne rend que les compteurs bruts.
                        const ctr = term.impressions > 0 ? (term.clicks / term.impressions) * 100 : 0;
                        const cpc = term.clicks > 0 ? term.cost / term.clicks : 0;
                        const panierMoyen = term.conversions > 0 ? term.conversionsValue / term.conversions : 0;
                        const roas = term.cost > 0 ? (term.conversionsValue / term.cost) * 100 : 0;
                        const roi = term.cost > 0 ? ((term.conversionsValue - term.cost) / term.cost) * 100 : 0;
                        const prevCtr = prevTerm && prevTerm.impressions > 0 ? (prevTerm.clicks / prevTerm.impressions) * 100 : 0;
                        const prevCpc = prevTerm && prevTerm.clicks > 0 ? prevTerm.cost / prevTerm.clicks : 0;
                        const prevPanier = prevTerm && prevTerm.conversions > 0 ? prevTerm.conversionsValue / prevTerm.conversions : 0;
                        const prevRoas = prevTerm && prevTerm.cost > 0 ? (prevTerm.conversionsValue / prevTerm.cost) * 100 : 0;
                        const prevRoi = prevTerm && prevTerm.cost > 0 ? ((prevTerm.conversionsValue - prevTerm.cost) / prevTerm.cost) * 100 : 0;

                        return (
                          <tr key={index}>
                            <td style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {term.searchTerm || term.term || 'Terme non spécifié'}
                            </td>
                            {isTopMetricVisible('impressions') && (
                              <td className="numeric">
                                {formatNumber(term.impressions || 0)}
                                {hasComparison && renderVariation(calculateVariation(term.impressions || 0, prevTerm.impressions || 0))}
                              </td>
                            )}
                            {isTopMetricVisible('clics') && (
                              <td className="numeric">
                                {formatNumber(term.clicks || 0)}
                                {hasComparison && renderVariation(calculateVariation(term.clicks || 0, prevTerm.clicks || 0))}
                              </td>
                            )}
                            {isTopMetricVisible('ctr') && (
                              <td className="numeric">
                                {formatNumber(ctr, 2)}%
                                {hasComparison && renderVariation(calculateVariation(ctr, prevCtr))}
                              </td>
                            )}
                            {isTopMetricVisible('cpc') && (
                              <td className="numeric">
                                €{formatNumber(cpc, 2)}
                                {hasComparison && renderVariation(calculateVariation(cpc, prevCpc), true)}
                              </td>
                            )}
                            {isTopMetricVisible('cout') && (
                              <td className="numeric">
                                €{formatNumber(term.cost || 0, 2)}
                                {hasComparison && renderVariation(calculateVariation(term.cost || 0, prevTerm.cost || 0), true)}
                              </td>
                            )}
                            {isTopMetricVisible('taux_conv') && (
                              <td className="numeric">
                                {formatNumber(term.conversionRate || 0, 2)}%
                                {hasComparison && renderVariation(calculateVariation(term.conversionRate || 0, prevConversionRate))}
                              </td>
                            )}
                            {isTopMetricVisible('conv') && (
                              <td className="numeric">
                                {formatNumber(term.conversions || 0, 1)}
                                {hasComparison && renderVariation(calculateVariation(term.conversions || 0, prevTerm.conversions || 0))}
                              </td>
                            )}
                            {isTopMetricVisible('cpa') && (
                              <td className={`numeric ${getCpaClassName(term.cpa || 0, selectedAccount?.id || 'global', moduleId)}`}>
                                {term.conversions > 0 ? `€${formatNumber(term.cpa || 0, 2)}` : '∞'}
                                {hasComparison && renderVariation(calculateVariation(term.cpa || 0, prevCpa), true)}
                              </td>
                            )}
                            {isTopMetricVisible('valeur_conv') && (
                              <td className="numeric">
                                €{formatNumber(term.conversionsValue || 0, 2)}
                                {hasComparison && renderVariation(calculateVariation(term.conversionsValue || 0, prevTerm.conversionsValue || 0))}
                              </td>
                            )}
                            {isTopMetricVisible('cos') && (
                              <td className={`numeric ${getCosClassName(term.cos || 0, selectedAccount?.id || 'global', moduleId)}`}>
                                {formatNumber(term.cos || 0, 1)}%
                                {hasComparison && renderVariation(calculateVariation(term.cos || 0, prevCos), true)}
                              </td>
                            )}
                            {isTopMetricVisible('panier_moyen') && (
                              <td className="numeric">
                                €{formatNumber(panierMoyen, 2)}
                                {hasComparison && renderVariation(calculateVariation(panierMoyen, prevPanier))}
                              </td>
                            )}
                            {isTopMetricVisible('roas') && (
                              <td className="numeric">
                                {formatNumber(roas, 1)}%
                                {hasComparison && renderVariation(calculateVariation(roas, prevRoas))}
                              </td>
                            )}
                            {isTopMetricVisible('roi') && (
                              <td className="numeric">
                                {formatNumber(roi, 1)}%
                                {hasComparison && renderVariation(calculateVariation(roi, prevRoi))}
                              </td>
                            )}
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Flop Termes de recherche */}
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '0 0 15px 0' }}>
                <h5 style={{ margin: 0, color: '#d32f2f', fontSize: '1.1em' }}>
                  <TrendingDown size={16} />&nbsp;Flop 5 Termes (Plus cliqués sans conversion)
                </h5>
                {userRole !== 'CLIENT' && (
                  <button
                    onClick={() => setShowMetricsConfigFlop(true)}
                    className="metrics-settings-btn"
                    title="Configurer les métriques"
                    style={{ padding: '4px', borderRadius: '4px' }}
                  >
                    <Settings size={14} />
                  </button>
                )}
              </div>
              <div className="campaigns-table-wrapper">
                <table className="campaigns-table">
                  <thead style={{ background: 'white' }}>
                    <tr>
                      <th style={{ background: 'white' }}>Terme de recherche</th>
                      {isFlopMetricVisible('impressions') && <th className="numeric" style={{ background: 'white' }}>Impr.</th>}
                      {isFlopMetricVisible('clics') && <th className="numeric" style={{ background: 'white' }}>Clics</th>}
                      {isFlopMetricVisible('ctr') && <th className="numeric" style={{ background: 'white' }}>CTR</th>}
                      {isFlopMetricVisible('cpc') && <th className="numeric" style={{ background: 'white' }}>CPC</th>}
                      {isFlopMetricVisible('cout') && <th className="numeric" style={{ background: 'white' }}>Coût</th>}
                      {isFlopMetricVisible('taux_conv') && <th className="numeric" style={{ background: 'white' }}>Taux conv.</th>}
                      {isFlopMetricVisible('conv') && <th className="numeric" style={{ background: 'white' }}>Conv.</th>}
                      {isFlopMetricVisible('cpa') && <th className="numeric" style={{ background: 'white' }}>CPA</th>}
                      {isFlopMetricVisible('valeur_conv') && <th className="numeric" style={{ background: 'white' }}>Valeur Conv.</th>}
                      {isFlopMetricVisible('cos') && <th className="numeric" style={{ background: 'white' }}>COS</th>}
                      {isFlopMetricVisible('panier_moyen') && <th className="numeric" style={{ background: 'white' }}>Panier moy.</th>}
                      {isFlopMetricVisible('roas') && <th className="numeric" style={{ background: 'white' }}>ROAS</th>}
                      {isFlopMetricVisible('roi') && <th className="numeric" style={{ background: 'white' }}>ROI</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredFlopSearchTerms.length === 0 ? (
                      <tr>
                        <td colSpan={1 + (isFlopMetricVisible('impressions') ? 1 : 0) + (isFlopMetricVisible('clics') ? 1 : 0) + (isFlopMetricVisible('ctr') ? 1 : 0) + (isFlopMetricVisible('cpc') ? 1 : 0) + (isFlopMetricVisible('cout') ? 1 : 0) + (isFlopMetricVisible('taux_conv') ? 1 : 0) + (isFlopMetricVisible('conv') ? 1 : 0) + (isFlopMetricVisible('cpa') ? 1 : 0) + (isFlopMetricVisible('valeur_conv') ? 1 : 0) + (isFlopMetricVisible('cos') ? 1 : 0) + (isFlopMetricVisible('panier_moyen') ? 1 : 0) + (isFlopMetricVisible('roas') ? 1 : 0) + (isFlopMetricVisible('roi') ? 1 : 0)} style={{ textAlign: 'center', color: '#666' }}>
                          Aucune donnée disponible
                        </td>
                      </tr>
                    ) : (
                      filteredFlopSearchTerms.map((term, index) => {
                        const prevTerm = findPreviousTerm(term);
                        const hasComparison = !!prevTerm;
                        // Métriques dérivées : le serveur ne rend que les compteurs bruts.
                        const ctr = term.impressions > 0 ? (term.clicks / term.impressions) * 100 : 0;
                        const cpc = term.clicks > 0 ? term.cost / term.clicks : 0;
                        const panierMoyen = term.conversions > 0 ? term.conversionsValue / term.conversions : 0;
                        const roas = term.cost > 0 ? (term.conversionsValue / term.cost) * 100 : 0;
                        const roi = term.cost > 0 ? ((term.conversionsValue - term.cost) / term.cost) * 100 : 0;
                        const prevConversionRate = prevTerm && prevTerm.clicks > 0 ? (prevTerm.conversions / prevTerm.clicks) * 100 : 0;
                        const prevCpa = prevTerm && prevTerm.conversions > 0 ? prevTerm.cost / prevTerm.conversions : 0;
                        const prevCos = prevTerm && prevTerm.conversionsValue > 0 ? (prevTerm.cost / prevTerm.conversionsValue) * 100 : 0;
                        const prevCtr = prevTerm && prevTerm.impressions > 0 ? (prevTerm.clicks / prevTerm.impressions) * 100 : 0;
                        const prevCpc = prevTerm && prevTerm.clicks > 0 ? prevTerm.cost / prevTerm.clicks : 0;
                        const prevPanier = prevTerm && prevTerm.conversions > 0 ? prevTerm.conversionsValue / prevTerm.conversions : 0;
                        const prevRoas = prevTerm && prevTerm.cost > 0 ? (prevTerm.conversionsValue / prevTerm.cost) * 100 : 0;
                        const prevRoi = prevTerm && prevTerm.cost > 0 ? ((prevTerm.conversionsValue - prevTerm.cost) / prevTerm.cost) * 100 : 0;

                        return (
                          <tr key={index}>
                            <td style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {term.searchTerm || term.term || 'Terme non spécifié'}
                            </td>
                            {isFlopMetricVisible('impressions') && (
                              <td className="numeric">
                                {formatNumber(term.impressions || 0)}
                                {hasComparison && renderVariation(calculateVariation(term.impressions || 0, prevTerm.impressions || 0))}
                              </td>
                            )}
                            {isFlopMetricVisible('clics') && (
                              <td className="numeric">
                                {formatNumber(term.clicks || 0)}
                                {hasComparison && renderVariation(calculateVariation(term.clicks || 0, prevTerm.clicks || 0))}
                              </td>
                            )}
                            {isFlopMetricVisible('ctr') && (
                              <td className="numeric">
                                {formatNumber(ctr, 2)}%
                                {hasComparison && renderVariation(calculateVariation(ctr, prevCtr))}
                              </td>
                            )}
                            {isFlopMetricVisible('cpc') && (
                              <td className="numeric">
                                €{formatNumber(cpc, 2)}
                                {hasComparison && renderVariation(calculateVariation(cpc, prevCpc), true)}
                              </td>
                            )}
                            {isFlopMetricVisible('cout') && (
                              <td className="numeric">
                                €{formatNumber(term.cost || 0, 2)}
                                {hasComparison && renderVariation(calculateVariation(term.cost || 0, prevTerm.cost || 0), true)}
                              </td>
                            )}
                            {isFlopMetricVisible('taux_conv') && (
                              <td className="numeric">
                                {formatNumber(term.conversionRate || 0, 2)}%
                                {hasComparison && renderVariation(calculateVariation(term.conversionRate || 0, prevConversionRate))}
                              </td>
                            )}
                            {isFlopMetricVisible('conv') && (
                              <td className="numeric">
                                {formatNumber(term.conversions || 0, 1)}
                                {hasComparison && renderVariation(calculateVariation(term.conversions || 0, prevTerm.conversions || 0))}
                              </td>
                            )}
                            {isFlopMetricVisible('cpa') && (
                              <td className={`numeric ${getCpaClassName(term.cpa || 0, selectedAccount?.id || 'global', moduleId)}`}>
                                {term.conversions > 0 ? `€${formatNumber(term.cpa || 0, 2)}` : '∞'}
                                {hasComparison && renderVariation(calculateVariation(term.cpa || 0, prevCpa), true)}
                              </td>
                            )}
                            {isFlopMetricVisible('valeur_conv') && (
                              <td className="numeric">
                                €{formatNumber(term.conversionsValue || 0, 2)}
                                {hasComparison && renderVariation(calculateVariation(term.conversionsValue || 0, prevTerm.conversionsValue || 0))}
                              </td>
                            )}
                            {isFlopMetricVisible('cos') && (
                              <td className={`numeric ${getCosClassName(term.cos || 0, selectedAccount?.id || 'global', moduleId)}`}>
                                {formatNumber(term.cos || 0, 1)}%
                                {hasComparison && renderVariation(calculateVariation(term.cos || 0, prevCos), true)}
                              </td>
                            )}
                            {isFlopMetricVisible('panier_moyen') && (
                              <td className="numeric">
                                €{formatNumber(panierMoyen, 2)}
                                {hasComparison && renderVariation(calculateVariation(panierMoyen, prevPanier))}
                              </td>
                            )}
                            {isFlopMetricVisible('roas') && (
                              <td className="numeric">
                                {formatNumber(roas, 1)}%
                                {hasComparison && renderVariation(calculateVariation(roas, prevRoas))}
                              </td>
                            )}
                            {isFlopMetricVisible('roi') && (
                              <td className="numeric">
                                {formatNumber(roi, 1)}%
                                {hasComparison && renderVariation(calculateVariation(roi, prevRoi))}
                              </td>
                            )}
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          )}
        </div>
      )}

      {/* Modal de configuration des métriques - Top */}
      <MetricsConfigModal
        isOpen={showMetricsConfigTop}
        onClose={() => setShowMetricsConfigTop(false)}
        componentId="topflop_terms_top"
        accountId={selectedAccount?.id || 'global'}
        componentTitle="Top 5 Termes (Plus convertisseurs)"
        hideApplyToAll={true}
      />

      {/* Modal de configuration des métriques - Flop */}
      <MetricsConfigModal
        isOpen={showMetricsConfigFlop}
        onClose={() => setShowMetricsConfigFlop(false)}
        componentId="topflop_terms_flop"
        accountId={selectedAccount?.id || 'global'}
        componentTitle="Flop 5 Termes (Plus cliqués sans conversion)"
        hideApplyToAll={true}
      />
    </div>
  );
});

TopFlopSearchTermsTable.displayName = 'TopFlopSearchTermsTable';

export default TopFlopSearchTermsTable;
